import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, Loader2 } from 'lucide-react';
import { useAppTheme } from '@/contexts/AppThemeContext';

export type ToastType = 'success' | 'error' | 'info' | 'loading';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components -- useToast is hook, ToastProvider is component
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { theme } = useAppTheme();

  const showToast = useCallback((type: ToastType, message: string, duration: number = 5000): string => {
    const id = Math.random().toString(36).substring(7);
    const toast: Toast = { id, type, message, duration };
    
    setToasts(prev => [...prev, toast]);
    
    if (duration > 0 && type !== 'loading') {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-emerald-400' : 'text-green-500'}`} />;
      case 'error':
        return <AlertCircle className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`} />;
      case 'info':
        return <Info className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'}`} />;
      case 'loading':
        return <Loader2 className={`h-5 w-5 flex-shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-500'} animate-spin`} />;
    }
  };

  const getBgColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return theme === 'dark' ? 'bg-emerald-950 border-emerald-700/80' : 'bg-green-50 border-green-200';
      case 'error':
        return theme === 'dark' ? 'bg-red-950 border-red-700/80' : 'bg-red-50 border-red-200';
      case 'info':
        return theme === 'dark' ? 'bg-blue-950 border-blue-700/80' : 'bg-blue-50 border-blue-200';
      case 'loading':
        return theme === 'dark' ? 'bg-zinc-800 border-zinc-600' : 'bg-gray-50 border-gray-200';
    }
  };

  const getTextColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return theme === 'dark' ? 'text-emerald-100' : 'text-green-800';
      case 'error':
        return theme === 'dark' ? 'text-red-100' : 'text-red-800';
      case 'info':
        return theme === 'dark' ? 'text-blue-100' : 'text-blue-800';
      case 'loading':
        return theme === 'dark' ? 'text-zinc-200' : 'text-gray-800';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, removeToast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg ${getBgColor(toast.type)} animate-in slide-in-from-bottom`}
            role="alert"
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${getTextColor(toast.type)}`}>
                {toast.message}
              </p>
            </div>
            {toast.type !== 'loading' && (
              <button
                onClick={() => removeToast(toast.id)}
                className={`flex-shrink-0 rounded p-1 ${theme === 'dark' ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/80' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}
                aria-label="Close notification"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
